<template>
  <div class="container-fluid py-3 productive-log-detail-page">
    <!-- Header -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <h5 class="mb-0">Productive vs Non-Productive — Detail View</h5>
    </div>

    <!-- Filters -->
    <div class="card mb-3 filter-card">
      <div class="card-body">
        <div class="row g-2">
          <!-- FSM -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> FSM </label>

            <select
              v-model="selectedFsm"
              class="form-select form-select-sm"
              :disabled="isFsmLocked || isBusy"
              @change="onFsmChange"
            >
              <option value="">Select FSM</option>

              <option v-if="isOtherUser" :value="ALL">All FSMs</option>

              <option
                v-for="fsm in fsmList"
                :key="fsm.profile_id"
                :value="String(fsm.profile_id)"
              >
                {{ fsm.name }}
              </option>
            </select>
          </div>

          <!-- ASM -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> ASM </label>

            <select
              v-model="selectedAsm"
              class="form-select form-select-sm"
              :disabled="isAsmLocked || isBusy"
              @change="onAsmChange"
            >
              <option value="">Select ASM</option>

              <option v-if="isFsmUser || isOtherUser" :value="ALL">
                All ASMs
              </option>

              <option
                v-for="asm in asmList"
                :key="asm.profile_id"
                :value="String(asm.profile_id)"
              >
                {{ asm.name }}
              </option>
            </select>
          </div>

          <!-- Distributor -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1">
              Distributor
            </label>

            <select
              v-model="selectedDistributor"
              class="form-select form-select-sm"
              :disabled="isDistributorLocked || isBusy"
              @change="onDistributorChange"
            >
              <option value="">Select Distributor</option>

              <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">
                All Distributors
              </option>

              <option
                v-for="distributor in distributorList"
                :key="distributor.profile_id"
                :value="String(distributor.profile_id)"
              >
                {{ distributor.name }}
              </option>
            </select>
          </div>

          <!-- Route -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> Route </label>

            <select
              v-model="selectedRoute"
              class="form-select form-select-sm"
              :disabled="!hasSpecificDistributor || isBusy"
              @change="onRouteChange"
            >
              <option value="">All Routes</option>

              <option
                v-for="route in routeList"
                :key="getRouteId(route)"
                :value="String(getRouteId(route))"
              >
                {{ getRouteName(route) }}
              </option>
            </select>
          </div>

          <!-- Dealer -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> Dealer </label>

            <select
              v-model="selectedDealer"
              class="form-select form-select-sm"
              :disabled="!selectedRoute || isBusy"
              @change="clearResults"
            >
              <option value="">All Dealers</option>

              <option
                v-for="dealer in dealerList"
                :key="getProfileId(dealer)"
                :value="String(getProfileId(dealer))"
              >
                {{ getDealerName(dealer) }}
              </option>
            </select>
          </div>

          <!-- DSR -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> DSR </label>

            <select
              v-model="selectedDsr"
              class="form-select form-select-sm"
              :disabled="!hasSpecificDistributor || isBusy"
              @change="clearResults"
            >
              <option value="">All DSRs</option>

              <option
                v-for="dsr in dsrList"
                :key="getProfileId(dsr)"
                :value="String(getProfileId(dsr))"
              >
                {{ getDsrName(dsr) }}
              </option>
            </select>
          </div>

          <!-- SE -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> SE </label>

            <select
              v-model="selectedSe"
              class="form-select form-select-sm"
              :disabled="!hasSpecificDistributor || isBusy"
              @change="clearResults"
            >
              <option value="">All SEs</option>

              <option
                v-for="se in seList"
                :key="getProfileId(se)"
                :value="String(getProfileId(se))"
              >
                {{ getStaffName(se, "SE") }}
              </option>
            </select>
          </div>

          <!-- Status -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1"> Status </label>

            <select
              v-model="selectedStatus"
              class="form-select form-select-sm"
              :disabled="isBusy"
              @change="clearResults"
            >
              <option
                v-for="status in statusOptions"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <!-- Date -->
          <div class="col-sm-6 col-md-3 col-xl-2">
            <label class="form-label small text-muted mb-1">
              Date
              <span class="required-mark">*</span>
            </label>

            <input
              v-model="selectedDate"
              type="date"
              class="form-control form-control-sm"
              :max="currentDate"
              :disabled="isBusy"
              @change="handleDateChange"
              @blur="validateDate(true)"
            />
          </div>

          <!-- Search -->
          <div class="col-sm-6 col-md-3 col-xl-2 d-flex align-items-end">
            <button
              type="button"
              class="btn btn-sm btn-primary w-100"
              :disabled="isBusy"
              @click="fetchData"
            >
              <i
                :class="
                  loading ? 'fa fa-spinner fa-spin me-1' : 'fa fa-search me-1'
                "
              ></i>

              {{ loading ? "Searching..." : "Search" }}
            </button>
          </div>

          <!-- Export -->
          <div class="col-sm-6 col-md-3 col-xl-2 d-flex align-items-end">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary w-100"
              :disabled="isBusy || tableData.length === 0"
              @click="exportExcel"
            >
              <i class="fa fa-download me-1"></i>
              Export Excel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial Loading -->
    <div v-if="initializing" class="d-flex justify-content-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden"> Loading... </span>
      </div>
    </div>

    <!-- Search Loading -->
    <div v-else-if="loading" class="d-flex justify-content-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden"> Loading... </span>
      </div>
    </div>

    <!-- Error / No Data -->
    <div v-else-if="searchedOnce && error" class="text-center py-5 text-muted">
      <img
        src="../../../../assets/vectors/data_not_found.png"
        alt="No data"
        class="no-data-image mb-2 d-block mx-auto"
      />

      <div>
        {{ error }}
      </div>
    </div>

    <!-- Initial State -->
    <div
      v-else-if="!searchedOnce"
      class="initial-state text-center py-5 text-muted"
    >
      <i class="fa fa-filter mb-2"></i>

      <div>Select the required filters and click Search.</div>
    </div>

    <!-- Result Table -->
    <div v-else-if="tableData.length" class="card productive-log-table-card">
      <div
        class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <span class="text-muted small">
          {{ tableData.length }} record(s)

          <template v-if="productColumns.length">
            · {{ productColumns.length }} product(s)
          </template>
        </span>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive productive-log-table-scroll">
          <table
            class="table table-bordered table-hover table-sm mb-0 productive-log-table"
          >
            <thead class="sticky-thead">
              <!-- Main Header -->
              <tr class="table-head-main">
                <!-- Dealer -->
                <th rowspan="2" class="col-dealer">Dealer</th>

                <!-- Date -->
                <th rowspan="2" class="col-date">Date</th>

                <!-- Created By -->
                <th rowspan="2" class="col-created-by">Created By</th>

                <!-- Route -->
                <th v-if="showRouteColumn" rowspan="2" class="col-route">
                  Route
                </th>

                <!-- Schedule -->
                <th rowspan="2" class="col-schedule">Schedule ID</th>

                <!-- Status -->
                <th rowspan="2" class="col-status">Status</th>

                <!-- Product Group -->
                <th
                  :colspan="productTableColumnCount"
                  class="product-group-header text-center"
                >
                  Products
                </th>

                <!-- Remarks -->
                <th rowspan="2" class="col-remarks">Remarks</th>
              </tr>

              <!-- Product Item Headers -->
              <tr class="table-head-products">
                <template v-if="productColumns.length">
                  <th
                    v-for="product in productColumns"
                    :key="product.column_key"
                    class="text-center col-product"
                    :title="product.variant_name"
                  >
                    {{ product.variant_name }}
                  </th>
                </template>

                <th v-else class="text-center col-product-empty">
                  No Products
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, index) in tableData" :key="row.row_key || index">
                <!-- Dealer -->
                <td class="dealer-cell">
                  {{ row.dealer_name || "—" }}
                </td>

                <!-- Date -->
                <td class="fw-semibold">
                  {{ formatDate(row.date) }}
                </td>

                <!-- Created By -->
                <td class="created-by-cell">
                  <div class="created-by-name">
                    {{ row.created_by_name || "—" }}
                  </div>

                  <span
                    v-if="
                      row.created_by_role && row.created_by_role !== 'Unknown'
                    "
                    class="created-by-role"
                  >
                    {{ row.created_by_role }}
                  </span>
                </td>

                <!-- Route -->
                <td v-if="showRouteColumn">
                  {{ row.route_name || "—" }}
                </td>

                <!-- Schedule -->
                <td>
                  {{ row.schedule_name || row.schedule_id || "—" }}
                </td>

                <!-- Status -->
                <td>
                  <span
                    class="status-badge"
                    :class="statusBadgeClass(row.status)"
                  >
                    {{ statusDisplayText(row.status) }}
                  </span>
                </td>

                <!-- Product Quantities -->
                <template v-if="productColumns.length">
                  <td
                    v-for="product in productColumns"
                    :key="product.column_key"
                    class="text-center product-quantity-cell"
                  >
                    <template v-if="isProductiveStatus(row.status)">
                      {{
                        formatQuantity(
                          row.product_quantities?.[product.column_key],
                        )
                      }}
                    </template>

                    <span v-else class="text-muted"> — </span>
                  </td>
                </template>

                <!-- Empty Product Column -->
                <td v-else class="text-center text-muted product-quantity-cell">
                  —
                </td>

                <!-- Remarks -->
                <td class="remarks-cell">
                  <!-- Non-Productive -->
                  <template v-if="isNonProductiveStatus(row.status)">
                    <div class="npc-remark-summary" style="display: flex; flex-direction: column; align-items: flex-start;">
                      <span
                        class="npc-reason-label"
                        :title="row.reason_name || 'Reason not available'"
                      >
                        {{ row.reason_name || "Reason not available" }}
                      </span>
                      <span v-if="row.remarks" class="npc-remarks-text text-muted" style="font-size: 0.85em; margin-top: 4px;">
                        ({{ row.remarks }})
                      </span>
                    </div>
                  </template>

                  <!-- Productive -->
                  <template v-else>
                    {{ row.remarks || "—" }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Non-Productive Reason / Remark Popup -->
    <transition name="npc-popup-fade">
      <div
        v-if="npcRemarkPopup.show"
        class="npc-popup-backdrop"
        role="presentation"
        @click.self="closeNpcRemarkPopup"
      >
        <div
          class="npc-popup-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="npcPopupTitle"
        >
          <!-- Header -->
          <div class="npc-popup-header">
            <div>
              <div id="npcPopupTitle" class="npc-popup-title">
                Non-Productive Details
              </div>

              <div v-if="npcRemarkPopup.dealerName" class="npc-popup-dealer">
                {{ npcRemarkPopup.dealerName }}
              </div>
            </div>

            <button
              type="button"
              class="npc-popup-close"
              aria-label="Close"
              @click="closeNpcRemarkPopup"
            >
              <i class="fa fa-times"></i>
            </button>
          </div>

          <!-- Content -->
          <div class="npc-popup-body">
            <div class="npc-popup-section">
              <div class="npc-popup-label">Reason</div>

              <div class="npc-popup-reason">
                {{ npcRemarkPopup.reason || "Reason not available" }}
              </div>
            </div>

            <div class="npc-popup-section">
              <div class="npc-popup-label">Remarks</div>

              <div class="npc-popup-remarks">
                {{
                  npcRemarkPopup.remarks || "No additional remarks provided."
                }}
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="npc-popup-footer">
            <button
              type="button"
              class="btn btn-sm btn-dark npc-popup-done"
              @click="closeNpcRemarkPopup"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";

import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchRouteList,
  getDealersByRoute,
  fetchSalesReps,
  fetchSalesExecutives,
  fetchProductiveNonProductiveLogsDetails,
} from "@/services/dataService";

export default {
  name: "ProductiveVsNonProductiveDetail",

  data() {
    const query = this.$route?.query || {};

    const currentDate = new Date().toLocaleDateString("en-CA");

    const now = new Date();
    const queryYear = Number(query.year);
    const queryMonth = Number(query.month);

    let selectedDate = currentDate;

    /*
     * Dashboard currently passes year and month.
     *
     * Current month:
     * use today's date.
     *
     * Previous month:
     * use the first date of that month.
     */
    if (
      typeof query.date === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(query.date)
    ) {
      selectedDate = query.date;
    } else if (
      Number.isInteger(queryYear) &&
      Number.isInteger(queryMonth) &&
      queryMonth >= 1 &&
      queryMonth <= 12
    ) {
      const isCurrentMonth =
        queryYear === now.getFullYear() && queryMonth === now.getMonth() + 1;

      selectedDate = isCurrentMonth
        ? currentDate
        : `${queryYear}-${String(queryMonth).padStart(2, "0")}-01`;
    }

    if (selectedDate > currentDate) {
      selectedDate = currentDate;
    }

    return {
      token: localStorage.getItem("token_set") || "",

      profileId: localStorage.getItem("profile_id") || "",

      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      loggedUserName: localStorage.getItem("business_name") || "Distributor",

      ALL: "__all__",

      userLabel: "",
      roleKey: "OTHER",

      initializing: false,
      filterLoading: false,
      loading: false,
      searchedOnce: false,
      error: null,

      fsmList: [],
      asmList: [],
      distributorList: [],
      routeList: [],
      dealerList: [],
      dsrList: [],
      seList: [],

      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedRoute: "",
      selectedDealer: "",
      selectedDsr: "",
      selectedSe: "",
      selectedStatus: "",
      selectedDate,

      currentDate,

      pendingDistributorId:
        query.distributor !== undefined ? String(query.distributor) : "",

      pendingRouteId: query.route !== undefined ? String(query.route) : "",

      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      statusOptions: [
        {
          value: "",
          label: "All",
        },
        {
          value: "productive",
          label: "Productive",
        },
        {
          value: "non_productive",
          label: "Non-Productive",
        },
      ],

      productColumns: [],
      tableData: [],

      productColumns: [],
      tableData: [],

      npcRemarkPopup: {
        show: false,
        dealerName: "",
        reason: "",
        remarks: "",
      },
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    isAsmUser() {
      return this.roleKey === "ASM";
    },

    isFsmUser() {
      return this.roleKey === "FSM";
    },

    isOtherUser() {
      return this.roleKey === "OTHER";
    },

    isBusy() {
      return this.initializing || this.filterLoading || this.loading;
    },

    hasSpecificDistributor() {
      return (
        Boolean(this.selectedDistributor) &&
        this.selectedDistributor !== this.ALL
      );
    },

    showRouteColumn() {
      return !this.selectedRoute;
    },

    productTableColumnCount() {
      return Math.max(this.productColumns.length, 1);
    },

    selectedStatusLabel() {
      return (
        this.statusOptions.find(
          (status) => status.value === this.selectedStatus,
        )?.label || "All"
      );
    },
  },

  async mounted() {
    window.addEventListener("keydown", this.handleNpcPopupKeydown);

    await this.initializePage();
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleNpcPopupKeydown);

    document.body.classList.remove("npc-popup-open");
  },

  methods: {
    showToast(type, message) {
      const toast = useToast();

      if (type === "success") {
        toast.success(message, {
          position: "top-right",
          timeout: 3000,
        });

        return;
      }

      toast.error(message, {
        position: "top-right",
        timeout: 3000,
      });
    },

    extractList(response) {
      const body = response?.data ?? response;

      if (Array.isArray(body)) {
        return body;
      }

      if (Array.isArray(body?.data)) {
        return body.data;
      }

      if (Array.isArray(body?.data?.data)) {
        return body.data.data;
      }

      return [];
    },

    extractRows(response) {
      const body = response?.data ?? response;

      if (Array.isArray(body?.data)) {
        return body.data;
      }

      if (Array.isArray(body?.data?.data)) {
        return body.data.data;
      }

      if (Array.isArray(body?.rows)) {
        return body.rows;
      }

      if (Array.isArray(body?.data?.rows)) {
        return body.data.rows;
      }

      if (Array.isArray(body?.logs)) {
        return body.logs;
      }

      return [];
    },

    extractProductColumns(response) {
      const body = response?.data ?? response;

      if (Array.isArray(body?.products)) {
        return body.products;
      }

      if (Array.isArray(body?.data?.products)) {
        return body.data.products;
      }

      return [];
    },

    extractMessage(response) {
      const body = response?.data ?? response;

      return body?.message || body?.error || "";
    },

    initRoleFromStorage() {
      const storedLabel =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(storedLabel || "");

      const label = this.userLabel.toUpperCase().trim();

      if (
        label === "DISTRIBUTOR" ||
        label.startsWith("DISTRIBUTOR") ||
        label === "DIS" ||
        label.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else if (label === "ASM" || label.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (label === "FSM" || label.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    async initializePage() {
      this.initializing = true;

      try {
        this.token = localStorage.getItem("token_set") || "";

        this.profileId = localStorage.getItem("profile_id") || "";

        this.myProfileId = Number(this.profileId || 0);

        this.initRoleFromStorage();

        if (!this.token) {
          this.error = "Session expired. Please log in again.";

          this.searchedOnce = true;
          return;
        }

        await this.loadFsmList();

        if (
          this.selectedFsm &&
          this.selectedAsm &&
          this.selectedDistributor &&
          this.selectedDate
        ) {
          await this.fetchData();
        }
      } catch (error) {
        console.error("Error initializing productivity detail page:", error);

        this.error = "Failed to initialize the page.";

        this.searchedOnce = true;
      } finally {
        this.initializing = false;
      }
    },

    async loadFsmList() {
      try {
        const response = await fetchFsmList(this.profileId, this.token);

        this.fsmList = this.extractList(response);

        let selectedFsm = null;

        if (this.isFsmUser) {
          selectedFsm =
            this.fsmList.find(
              (fsm) => Number(fsm.profile_id) === this.myProfileId,
            ) || (this.fsmList.length === 1 ? this.fsmList[0] : null);
        } else if (this.isAsmUser || this.isDistributorUser) {
          selectedFsm = this.fsmList.length === 1 ? this.fsmList[0] : null;
        }

        if (selectedFsm) {
          this.selectedFsm = String(selectedFsm.profile_id);

          this.isFsmLocked = true;

          await this.loadAsmList();
        }
      } catch (error) {
        console.error("Error loading FSM list:", error);

        this.fsmList = [];

        this.showToast("error", "Failed to load FSM list.");
      }
    },

    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.selectedRoute = "";
      this.selectedDealer = "";
      this.selectedDsr = "";
      this.selectedSe = "";

      this.asmList = [];
      this.distributorList = [];
      this.routeList = [];
      this.dealerList = [];
      this.dsrList = [];
      this.seList = [];

      this.isAsmLocked = false;
      this.isDistributorLocked = false;

      this.clearResults();

      if (!this.selectedFsm) {
        return;
      }

      if (this.selectedFsm === this.ALL) {
        this.selectedAsm = this.ALL;
        this.selectedDistributor = this.ALL;

        this.pendingDistributorId = "";
        this.pendingRouteId = "";

        return;
      }

      await this.loadAsmList();
    },

    async loadAsmList() {
      try {
        const response = await fetchAsmList(
          this.profileId,
          this.selectedFsm,
          this.token,
        );

        this.asmList = this.extractList(response);

        let selectedAsm = null;

        if (this.isAsmUser) {
          selectedAsm =
            this.asmList.find(
              (asm) => Number(asm.profile_id) === this.myProfileId,
            ) || (this.asmList.length === 1 ? this.asmList[0] : null);
        } else if (this.isDistributorUser && this.asmList.length === 1) {
          selectedAsm = this.asmList[0];
        }

        if (selectedAsm) {
          this.selectedAsm = String(selectedAsm.profile_id);

          this.isAsmLocked = true;

          await this.loadDistributorList();
        }
      } catch (error) {
        console.error("Error loading ASM list:", error);

        this.asmList = [];

        this.showToast("error", "Failed to load ASM list.");
      }
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.selectedRoute = "";
      this.selectedDealer = "";
      this.selectedDsr = "";
      this.selectedSe = "";

      this.distributorList = [];
      this.routeList = [];
      this.dealerList = [];
      this.dsrList = [];
      this.seList = [];

      this.isDistributorLocked = false;

      this.clearResults();

      if (!this.selectedAsm) {
        return;
      }

      if (this.selectedAsm === this.ALL) {
        this.selectedDistributor = this.ALL;

        this.pendingDistributorId = "";
        this.pendingRouteId = "";

        return;
      }

      await this.loadDistributorList();
    },

    async loadDistributorList() {
      try {
        const response = await fetchDistributorList(
          this.profileId,
          this.selectedAsm,
          this.token,
        );

        let list = this.extractList(response);

        this.isDistributorLocked = false;

        if (this.isDistributorUser && this.myProfileId) {
          const ownDistributor = list.find(
            (distributor) =>
              Number(distributor.profile_id) === this.myProfileId,
          );

          if (ownDistributor) {
            list = [ownDistributor];

            this.selectedDistributor = String(ownDistributor.profile_id);

            this.isDistributorLocked = true;
          }
        } else if (this.pendingDistributorId) {
          const requestedDistributor = list.find(
            (distributor) =>
              String(distributor.profile_id) ===
              String(this.pendingDistributorId),
          );

          if (requestedDistributor) {
            this.selectedDistributor = String(requestedDistributor.profile_id);
          }
        }

        this.distributorList = list;

        if (this.hasSpecificDistributor) {
          await Promise.all([
            this.loadRouteList(),
            this.loadDsrList(),
            this.loadSeList(),
          ]);
        }

        this.pendingDistributorId = "";
      } catch (error) {
        console.error("Error loading Distributor list:", error);

        this.distributorList = [];
        this.selectedDistributor = "";
        this.isDistributorLocked = false;

        this.showToast("error", "Failed to load Distributor list.");
      }
    },

    async onDistributorChange() {
      this.selectedRoute = "";
      this.selectedDealer = "";
      this.selectedDsr = "";
      this.selectedSe = "";

      this.routeList = [];
      this.dealerList = [];
      this.dsrList = [];
      this.seList = [];

      this.pendingRouteId = "";

      this.clearResults();

      if (!this.hasSpecificDistributor) {
        return;
      }

      this.filterLoading = true;

      try {
        await Promise.all([
          this.loadRouteList(),
          this.loadDsrList(),
          this.loadSeList(),
        ]);
      } finally {
        this.filterLoading = false;
      }
    },

    async loadRouteList() {
      this.routeList = [];

      if (!this.hasSpecificDistributor) {
        return;
      }

      try {
        const response = await fetchRouteList(
          this.selectedDistributor,
          this.token,
        );

        this.routeList = this.extractList(response);

        if (this.pendingRouteId) {
          const requestedRoute = this.routeList.find(
            (route) =>
              String(this.getRouteId(route)) === String(this.pendingRouteId),
          );

          if (requestedRoute) {
            this.selectedRoute = String(this.getRouteId(requestedRoute));

            await this.loadDealerListByRoute();
          }
        }

        this.pendingRouteId = "";
      } catch (error) {
        console.error("Error loading Route list:", error);

        this.routeList = [];

        this.showToast("error", "Failed to load Route list.");
      }
    },

    async onRouteChange() {
      this.selectedDealer = "";
      this.dealerList = [];

      this.clearResults();

      if (!this.selectedRoute) {
        return;
      }

      this.filterLoading = true;

      try {
        await this.loadDealerListByRoute();
      } finally {
        this.filterLoading = false;
      }
    },

    async loadDealerListByRoute() {
      this.dealerList = [];

      if (!this.selectedRoute) {
        return;
      }

      try {
        const response = await getDealersByRoute(
          Number(this.selectedRoute),
          this.token,
        );

        this.dealerList = this.extractList(response);
      } catch (error) {
        console.error("Error loading Dealers by Route:", error);

        this.dealerList = [];

        this.showToast("error", "Failed to load Dealer list.");
      }
    },

    async loadDsrList() {
      this.dsrList = [];

      if (!this.hasSpecificDistributor) {
        return;
      }

      try {
        const response = await fetchSalesReps(
          this.profileId,
          this.selectedDistributor,
          this.token,
        );

        this.dsrList = this.extractList(response);
      } catch (error) {
        console.error("Error loading DSR list:", error);

        this.dsrList = [];

        this.showToast("error", "Failed to load DSR list.");
      }
    },

    async loadSeList() {
      this.seList = [];

      if (!this.hasSpecificDistributor) {
        return;
      }

      try {
        const response = await fetchSalesExecutives(
          this.profileId,
          this.selectedDistributor,
          this.token,
        );

        this.seList = this.extractList(response);
      } catch (error) {
        console.error("Error loading SE list:", error);

        this.seList = [];

        this.showToast("error", "Failed to load SE list.");
      }
    },


    clearResults() {
      this.productColumns = [];
      this.tableData = [];
      this.error = null;
      this.searchedOnce = false;
    },

    isValidISODate(value) {
      if (!value || typeof value !== "string" || value.includes("/")) {
        return false;
      }

      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (!match) {
        return false;
      }

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      const date = new Date(year, month - 1, day);

      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    },

    validateDate(showMessage = true) {
      if (!this.selectedDate) {
        if (showMessage) {
          this.showToast("error", "Please select a valid Date.");
        }

        return false;
      }

      if (!this.isValidISODate(this.selectedDate)) {
        if (showMessage) {
          this.showToast("error", "Invalid Date.");
        }

        this.selectedDate = "";
        return false;
      }

      if (this.selectedDate > this.currentDate) {
        if (showMessage) {
          this.showToast("error", "Date cannot be in the future.");
        }

        this.selectedDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      this.clearResults();
      this.validateDate(false);
    },

    validateFilters() {
      if (!this.selectedFsm) {
        this.showToast("error", "Please select FSM.");

        return false;
      }

      if (!this.selectedAsm) {
        this.showToast("error", "Please select ASM.");

        return false;
      }

      if (!this.selectedDistributor) {
        this.showToast("error", "Please select Distributor.");

        return false;
      }

      if (this.isDistributorUser && this.selectedDistributor === this.ALL) {
        this.showToast("error", "Please select Distributor.");

        return false;
      }

      return this.validateDate(true);
    },

    async fetchData() {
      if (!this.validateFilters()) {
        return;
      }

      this.token = localStorage.getItem("token_set") || "";

      if (!this.token) {
        this.showToast("error", "Session expired. Please log in again.");

        return;
      }

      this.loading = true;
      this.searchedOnce = true;
      this.error = null;
      this.productColumns = [];
      this.tableData = [];

      try {
        const payload = {
          profile_id: this.profileId,

          fsm_profile_id: this.selectedFsm === this.ALL ? "" : this.selectedFsm,

          asm_profile_id: this.selectedAsm === this.ALL ? "" : this.selectedAsm,

          distributor_profile_id:
            this.selectedDistributor === this.ALL
              ? ""
              : this.selectedDistributor,

          route_id: this.selectedRoute || "",

          dealer_profile_id: this.selectedDealer || "",

          dsr_profile_id: this.selectedDsr || "",

          se_profile_id: this.selectedSe || "",

          status: this.selectedStatus || "",

          date: this.selectedDate,
        };

        const response = await fetchProductiveNonProductiveLogsDetails(
          payload,
          this.token,
        );

        const body = response?.data ?? response;

        const explicitlyFailed =
          body?.success === false || body?.status === false;

        if (explicitlyFailed) {
          this.error =
            this.extractMessage(response) ||
            "Failed to load productive and non-productive logs.";

          return;
        }

        const products = this.extractProductColumns(response);

        const rows = this.extractRows(response);

        this.productColumns = products
          .map((product) => this.normalizeProductColumn(product))
          .filter((product) => product.column_key);

        if (!rows.length) {
          this.error =
            this.extractMessage(response) ||
            "No data available for the selected filters.";

          return;
        }

        this.tableData = rows.map((row, index) =>
          this.normalizeResultRow(row, index),
        );
      } catch (error) {
        console.error("Error loading Productive / Non-Productive logs:", error);

        this.error =
          error?.response?.data?.message ||
          "Something went wrong. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    normalizeProductColumn(product) {
      const variantId = product?.variant_id ?? product?.id ?? "";

      const variantName =
        product?.variant_name ??
        product?.item_name ??
        product?.name ??
        `Product ${variantId}`;

      const columnKey =
        product?.column_key || (variantId !== "" ? `variant_${variantId}` : "");

      return {
        variant_id: variantId,
        variant_name: variantName,
        column_key: columnKey,
      };
    },

    normalizeResultRow(row, index) {
      return {
        row_key:
          row?.row_key ??
          row?.id ??
          row?.source_id ??
          `${index}-${this.selectedDate}`,

        source_id: row?.source_id ?? row?.id ?? null,

        dealer_id: row?.dealer_id ?? null,

        dealer_name:
          row?.dealer_name ??
          row?.business_display_name ??
          row?.business_name ??
          row?.dealer ??
          "—",

        date:
          row?.date ??
          row?.activity_date ??
          row?.invoice_date ??
          row?.visited_at ??
          this.selectedDate,

        created_by_id: row?.created_by_id ?? row?.activity_by ?? null,

        created_by_name:
          row?.created_by_name ??
          row?.creator_name ??
          row?.visited_by_name ??
          row?.invoice_created_by_name ??
          "Unknown",

        created_by_role: this.normalizeCreatedByRole(
          row?.created_by_role ??
            row?.creator_role ??
            row?.visited_by_role ??
            row?.invoice_created_by_role,
        ),

        route_id: row?.route_id ?? null,

        route_name: row?.route_name ?? row?.route ?? "—",

        schedule_id: row?.schedule_id ?? null,

        schedule_name: row?.schedule_name ?? row?.delivery_schedule_name ?? "",

        status: this.normalizeStatus(row?.status ?? row?.is_productive),

        product_quantities: this.normalizeProductQuantities(
          row?.product_quantities ?? row?.products ?? {},
        ),

        reason_id: row?.reason_id ?? null,

        reason_name: row?.reason_name ?? row?.reason ?? "",

        remarks: row?.remarks ?? row?.remark ?? row?.reason ?? "",
      };
    },

    normalizeProductQuantities(value) {
      if (!value) {
        return {};
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        return value;
      }

      if (!Array.isArray(value)) {
        return {};
      }

      return value.reduce((result, product) => {
        const variantId =
          product?.variant_id ?? product?.item_id ?? product?.id;

        const columnKey =
          product?.column_key ||
          (variantId !== undefined && variantId !== null
            ? `variant_${variantId}`
            : "");

        if (!columnKey) {
          return result;
        }

        result[columnKey] =
          product?.quantity ?? product?.item_qty ?? product?.qty ?? 0;

        return result;
      }, {});
    },

    normalizeCreatedByRole(value) {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return "Unknown";
      }

      const originalRole = String(value).trim();

      const normalizedRole = originalRole.toUpperCase().replace(/[\s-]+/g, "_");

      /*
       * Backend role REP must be displayed as DSR.
       */
      if (
        normalizedRole === "REP" ||
        normalizedRole === "SALES_REP" ||
        normalizedRole === "SALES_REPRESENTATIVE"
      ) {
        return "DSR";
      }

      return originalRole;
    },

    normalizeStatus(value) {
      if (value === true || value === 1 || value === "1") {
        return "productive";
      }

      if (value === false || value === 0 || value === "0") {
        return "non_productive";
      }

      const normalized = String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_");

      if (normalized === "productive") {
        return "productive";
      }

      if (normalized === "non_productive" || normalized === "nonproductive") {
        return "non_productive";
      }

      return normalized || "unknown";
    },

    isProductiveStatus(status) {
      return this.normalizeStatus(status) === "productive";
    },

    isNonProductiveStatus(status) {
      return this.normalizeStatus(status) === "non_productive";
    },

    openNpcRemarkPopup(row) {
      this.npcRemarkPopup = {
        show: true,

        dealerName: row?.dealer_name || "",

        reason: row?.reason_name || "Reason not available",

        remarks: row?.remarks || "",
      };

      document.body.classList.add("npc-popup-open");
    },

    closeNpcRemarkPopup() {
      this.npcRemarkPopup = {
        show: false,
        dealerName: "",
        reason: "",
        remarks: "",
      };

      document.body.classList.remove("npc-popup-open");
    },

    handleNpcPopupKeydown(event) {
      if (event.key === "Escape" && this.npcRemarkPopup.show) {
        this.closeNpcRemarkPopup();
      }
    },

    statusDisplayText(status) {
      const normalized = this.normalizeStatus(status);

      if (normalized === "productive") {
        return "Productive";
      }

      if (normalized === "non_productive") {
        return "Non-Productive";
      }

      return "Unknown";
    },

    statusBadgeClass(status) {
      const normalized = this.normalizeStatus(status);

      return {
        "status-productive": normalized === "productive",

        "status-non-productive": normalized === "non_productive",

        "status-unknown":
          normalized !== "productive" && normalized !== "non_productive",
      };
    },

    formatDate(value) {
      if (!value) {
        return "—";
      }

      const text = String(value);

      const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
      }

      const date = new Date(text);

      if (Number.isNaN(date.getTime())) {
        return text;
      }

      return date.toLocaleDateString("en-CA");
    },

    formatQuantity(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }

      const number = Number(value);

      if (!Number.isFinite(number)) {
        return value;
      }

      if (Number.isInteger(number)) {
        return number.toLocaleString("en-US");
      }

      return number.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
    },

    formatCreatedBy(row) {
      const name =
        row?.created_by_name && row.created_by_name !== "Unknown"
          ? row.created_by_name
          : "—";

      const role =
        row?.created_by_role && row.created_by_role !== "Unknown"
          ? row.created_by_role
          : "";

      return role ? `${name} (${role})` : name;
    },

    getProfileId(item) {
      return item?.profile_id ?? item?.id ?? "";
    },

    getRouteId(route) {
      return route?.route_id ?? route?.id ?? "";
    },

    getRouteName(route) {
      return (
        route?.route_name ?? route?.name ?? `Route #${this.getRouteId(route)}`
      );
    },

    getDealerName(dealer) {
      return (
        dealer?.business_display_name ??
        dealer?.business_name ??
        dealer?.name ??
        dealer?.full_name ??
        `Dealer #${this.getProfileId(dealer)}`
      );
    },

    getDsrName(dsr) {
      return (
        dsr?.vehicle_no ??
        dsr?.vehicle_number ??
        dsr?.registration_no ??
        dsr?.name ??
        dsr?.full_name ??
        dsr?.first_name ??
        `DSR #${this.getProfileId(dsr)}`
      );
    },

    getStaffName(staff, fallbackLabel = "User") {
      const firstName = staff?.first_name || "";

      const lastName = staff?.last_name || "";

      const combinedName = `${firstName} ${lastName}`.trim();

      return (
        staff?.name ||
        staff?.full_name ||
        combinedName ||
        `${fallbackLabel} #${this.getProfileId(staff)}`
      );
    },

    getSelectedName(list, selectedId, fallback, type = "normal") {
      if (!selectedId || selectedId === this.ALL) {
        return fallback;
      }

      const selectedItem = list.find((item) => {
        const id = item?.profile_id ?? item?.route_id ?? item?.id;

        return String(id) === String(selectedId);
      });

      if (!selectedItem) {
        return fallback;
      }

      if (type === "route") {
        return this.getRouteName(selectedItem);
      }

      if (type === "dealer") {
        return this.getDealerName(selectedItem);
      }

      if (type === "dsr") {
        return this.getDsrName(selectedItem);
      }

      return this.getStaffName(selectedItem, fallback);
    },

    exportExcel() {
      if (!this.tableData.length) {
        this.showToast("error", "No data available to export.");

        return;
      }

      const filterRows = [
        ["Productive vs Non-Productive Logs"],
        [],
        [
          "FSM",
          this.getSelectedName(this.fsmList, this.selectedFsm, "All FSMs"),
        ],
        [
          "ASM",
          this.getSelectedName(this.asmList, this.selectedAsm, "All ASMs"),
        ],
        [
          "Distributor",
          this.isDistributorUser
            ? this.loggedUserName
            : this.getSelectedName(
                this.distributorList,
                this.selectedDistributor,
                "All Distributors",
              ),
        ],
        [
          "Route",
          this.getSelectedName(
            this.routeList,
            this.selectedRoute,
            "All Routes",
            "route",
          ),
        ],
        [
          "Dealer",
          this.getSelectedName(
            this.dealerList,
            this.selectedDealer,
            "All Dealers",
            "dealer",
          ),
        ],
        [
          "DSR",
          this.getSelectedName(
            this.dsrList,
            this.selectedDsr,
            "All DSRs",
            "dsr",
          ),
        ],
        ["SE", this.getSelectedName(this.seList, this.selectedSe, "All SEs")],
        ["Status", this.selectedStatusLabel],
        ["Date", this.selectedDate],
        [],
      ];

      const leadingHeaders = [
        "Dealer",
        "Date",
        "Created By",

        ...(this.showRouteColumn ? ["Route"] : []),

        "Schedule ID",
        "Status",
      ];

      const exportedProductHeaders = this.productColumns.length
        ? this.productColumns.map((product) => product.variant_name)
        : ["No Products"];

      const productStartColumn = leadingHeaders.length;

      const remarksColumn = productStartColumn + exportedProductHeaders.length;

      const groupHeaderRow = [
        ...leadingHeaders,
        "Products",

        ...Array(Math.max(exportedProductHeaders.length - 1, 0)).fill(""),

        "Remarks",
      ];

      const productHeaderRow = [
        ...leadingHeaders.map(() => ""),

        ...exportedProductHeaders,

        "",
      ];

      const tableRows = this.tableData.map((row) => [
        row.dealer_name || "—",

        this.formatDate(row.date),

        this.formatCreatedBy(row),

        ...(this.showRouteColumn ? [row.route_name || "—"] : []),

        row.schedule_name || row.schedule_id || "—",

        this.statusDisplayText(row.status),

        ...(this.productColumns.length
          ? this.productColumns.map((product) =>
              this.isProductiveStatus(row.status)
                ? Number(row.product_quantities?.[product.column_key] || 0)
                : "",
            )
          : [""]),

        this.isNonProductiveStatus(row.status)
          ? `${row.reason_name || "Reason not available"}${row.remarks ? ` (${row.remarks})` : ""}`
          : row.remarks || "",
      ]);

      const firstName = localStorage.getItem("first_name") || "";

      const lastName = localStorage.getItem("last_name") || "";

      const generatedBy = `${firstName} ${lastName}`.trim() || "—";

      const footerRows = [
        [],
        ["Generated By", generatedBy],
        [
          "Generated At",
          new Date().toLocaleString("en-US", {
            hour12: false,
          }),
        ],
      ];

      const rows = [
        ...filterRows,
        groupHeaderRow,
        productHeaderRow,
        ...tableRows,
        ...footerRows,
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(rows);

      const groupHeaderRowIndex = filterRows.length;

      const productHeaderRowIndex = groupHeaderRowIndex + 1;

      const merges = [];

      /*
       * Merge Dealer, Date, Created By, Route,
       * Schedule ID and Status vertically.
       */
      for (
        let columnIndex = 0;
        columnIndex < leadingHeaders.length;
        columnIndex++
      ) {
        merges.push({
          s: {
            r: groupHeaderRowIndex,
            c: columnIndex,
          },
          e: {
            r: productHeaderRowIndex,
            c: columnIndex,
          },
        });
      }

      /*
       * Merge the Products parent heading horizontally.
       */
      if (exportedProductHeaders.length > 1) {
        merges.push({
          s: {
            r: groupHeaderRowIndex,
            c: productStartColumn,
          },
          e: {
            r: groupHeaderRowIndex,
            c: productStartColumn + exportedProductHeaders.length - 1,
          },
        });
      }

      /*
       * Merge Remarks vertically.
       */
      merges.push({
        s: {
          r: groupHeaderRowIndex,
          c: remarksColumn,
        },
        e: {
          r: productHeaderRowIndex,
          c: remarksColumn,
        },
      });

      worksheet["!merges"] = merges;

      worksheet["!cols"] = [
        { wch: 30 },
        { wch: 15 },
        { wch: 28 },

        ...(this.showRouteColumn ? [{ wch: 22 }] : []),

        { wch: 24 },
        { wch: 18 },

        ...exportedProductHeaders.map(() => ({
          wch: 17,
        })),

        { wch: 45 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Productivity Logs");

      XLSX.writeFile(
        workbook,
        `Productive_Non_Productive_Logs_${this.selectedDate}.xlsx`,
      );

      this.showToast("success", "Excel file exported successfully.");
    },
  },
};
</script>

<style scoped>
.productive-log-detail-page {
  min-width: 0;
}

.filter-card {
  position: relative;
  z-index: 2;
  overflow: visible;
}

.required-mark {
  color: #dc3545;
}

.initial-state {
  min-height: 220px;
}

.initial-state i {
  color: #adb5bd;
  font-size: 24px;
}

.no-data-image {
  height: 80px;
}

/* =========================================================
   TABLE CARD
========================================================= */

.productive-log-table-card {
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
  border-radius: 12px;
}

.productive-log-table-scroll {
  position: relative;
  z-index: 1;
  isolation: isolate;

  max-height: 500px;
  overflow: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;

  border-radius: 0 0 12px 12px;
  background: #ffffff;
  scrollbar-gutter: stable;

  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.78) #f1f5f9;

  box-shadow: inset -10px 0 14px -14px rgba(15, 23, 42, 0.32),
    inset 0 -10px 14px -14px rgba(15, 23, 42, 0.32);
}

/* Chrome / Edge / Safari */
.productive-log-table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.productive-log-table-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 999px;
}

.productive-log-table-scroll::-webkit-scrollbar-track:horizontal {
  background: #e2e8f0;
}

.productive-log-table-scroll::-webkit-scrollbar-thumb {
  min-width: 42px;
  min-height: 42px;
  border: 2px solid #f1f5f9;
  border-radius: 999px;

  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);

  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
}

.productive-log-table-scroll::-webkit-scrollbar-thumb:hover {
  border-color: #e2e8f0;

  background: linear-gradient(180deg, #64748b 0%, #475569 100%);
}

.productive-log-table-scroll::-webkit-scrollbar-thumb:active {
  background: linear-gradient(180deg, #475569 0%, #334155 100%);
}

.productive-log-table-scroll::-webkit-scrollbar-corner {
  background: #e2e8f0;
}

/* =========================================================
   TABLE
========================================================= */

.productive-log-table {
  min-width: max-content;
  margin-bottom: 0 !important;
  border-collapse: collapse !important;
  background: #ffffff;
}

.productive-log-table th,
.productive-log-table td {
  padding: 10px 12px;
  border-color: #e8edf4 !important;
  font-size: 0.82rem;
  vertical-align: middle;
  white-space: nowrap;
}

.productive-log-table tbody tr {
  transition: background-color 0.18s ease;
}

.productive-log-table tbody tr:hover {
  background-color: #f8fafc;
}

/* =========================================================
   STICKY TWO-ROW HEADER
========================================================= */

.sticky-thead th {
  position: sticky;
  z-index: 4;

  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);

  color: #334155;
  border-bottom: 1px solid #dbe3ef;

  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.06);

  font-weight: 700;
  vertical-align: middle;
}

.table-head-main th {
  top: 0;
  z-index: 6;
}

.table-head-products th {
  top: 39px;
  z-index: 5;
}

.product-group-header {
  min-width: 160px;

  background: linear-gradient(180deg, #f5f8fc 0%, #edf3f9 100%) !important;

  color: #344054 !important;
  letter-spacing: 0.01em;
}

.table-head-products th {
  background-color: #f8fafc !important;
  color: #475467;
  font-size: 0.76rem;
  font-weight: 650;
}

/* =========================================================
   TABLE COLUMNS
========================================================= */

.col-dealer {
  min-width: 230px;
}

.col-date {
  min-width: 135px;
}

.col-created-by {
  min-width: 210px;
}

.col-route {
  min-width: 180px;
}

.col-schedule {
  min-width: 190px;
}

.col-status {
  min-width: 145px;
}

.col-product {
  min-width: 140px;
  max-width: 190px;
}

.col-product-empty {
  min-width: 150px;
}

.col-remarks {
  min-width: 340px;
}

.dealer-cell {
  color: #333333;
  font-weight: 500;
}

/* =========================================================
   CREATED BY
========================================================= */

.created-by-cell {
  min-width: 210px;
}

.created-by-name {
  color: #344054;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.3;
}

.created-by-role {
  display: inline-flex;
  align-items: center;

  margin-top: 3px;
  padding: 2px 7px;

  border: 1px solid #d8e1ec;
  border-radius: 4px;

  background-color: #f3f6fa;
  color: #475467;

  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
}

/* =========================================================
   PRODUCT QUANTITY
========================================================= */

.product-quantity-cell {
  color: #334155;
  font-weight: 600;
}

/* =========================================================
   STATUS BADGES
========================================================= */

.status-badge {
  min-width: 110px;
  padding: 5px 9px;
  border-radius: 5px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 0.72rem;
  font-weight: 650;
  line-height: 1.2;
  white-space: nowrap;
}

.status-productive {
  border: 1px solid #b7dfc7;
  background-color: #e8f6ee;
  color: #1f7a47;
}

.status-non-productive {
  border: 1px solid #f0c2c8;
  background-color: #fcecef;
  color: #b42336;
}

.status-unknown {
  border: 1px solid #d7dde5;
  background-color: #f3f5f7;
  color: #667085;
}

/* =========================================================
   REMARKS
========================================================= */

.remarks-cell {
  min-width: 340px;
  max-width: 450px;

  color: #475467;
  line-height: 1.45;

  white-space: normal !important;
  word-break: break-word;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 768px) {
  .productive-log-detail-page {
    padding-right: 8px;
    padding-left: 8px;
  }

  .productive-log-table-scroll {
    max-height: 72vh;
  }

  .productive-log-table th,
  .productive-log-table td {
    font-size: 12px;
  }

  .table-head-products th {
    top: 38px;
  }
}

/* =========================================================
   NPC REASON / REMARK
========================================================= */

.npc-remark-summary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
}

.npc-reason-label {
  display: inline-block;
  max-width: 250px;
  overflow: hidden;

  color: #475467;
  font-size: 0.78rem;
  font-weight: 600;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.npc-remark-button {
  position: relative;

  width: 27px;
  height: 27px;
  flex: 0 0 27px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #d6deea;
  border-radius: 6px;

  background-color: #f8fafc;
  color: #475467;

  font-size: 11px;
  cursor: pointer;

  transition: background-color 0.18s ease, border-color 0.18s ease,
    color 0.18s ease, transform 0.18s ease;
}

.npc-remark-button:hover {
  border-color: #b9c5d5;
  background-color: #eef3f8;
  color: #1d2939;
  transform: translateY(-1px);
}

.npc-remark-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(71, 84, 103, 0.12);
}

/* Small custom tooltip */
/* .npc-remark-button::before {
  content: attr(data-tooltip);

  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 30;

  width: max-content;
  max-width: 180px;
  padding: 5px 8px;

  border-radius: 5px;
  background-color: #1f2937;
  color: #ffffff;

  font-size: 10px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transform: translateY(3px);

  transition:
    opacity 0.16s ease,
    visibility 0.16s ease,
    transform 0.16s ease;
} */

.npc-remark-button:hover::before,
.npc-remark-button:focus::before {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* =========================================================
   SMALL PREMIUM POPUP
========================================================= */

.npc-popup-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 18px;

  background-color: rgba(15, 23, 42, 0.42);

  backdrop-filter: blur(3px);
}

.npc-popup-card {
  width: 100%;
  max-width: 410px;
  overflow: hidden;

  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 13px;

  background-color: #ffffff;

  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.2),
    0 4px 14px rgba(15, 23, 42, 0.08);
}

.npc-popup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;

  padding: 16px 17px 13px;

  border-bottom: 1px solid #edf1f5;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.npc-popup-title {
  color: #1d2939;
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.3;
}

.npc-popup-dealer {
  margin-top: 3px;

  color: #667085;
  font-size: 0.73rem;
  font-weight: 500;
}

.npc-popup-close {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #e0e6ed;
  border-radius: 7px;

  background-color: #ffffff;
  color: #667085;

  font-size: 11px;
  cursor: pointer;

  transition: background-color 0.16s ease, color 0.16s ease;
}

.npc-popup-close:hover {
  background-color: #f2f4f7;
  color: #1d2939;
}

.npc-popup-body {
  padding: 16px 17px;
}

.npc-popup-section + .npc-popup-section {
  margin-top: 15px;
}

.npc-popup-label {
  margin-bottom: 6px;

  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;

  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.npc-popup-reason {
  display: inline-flex;
  padding: 5px 9px;

  border: 1px solid #f1c9ce;
  border-radius: 6px;

  background-color: #fdf0f2;
  color: #a9283a;

  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.35;
}

.npc-popup-remarks {
  max-height: 150px;
  overflow-y: auto;

  padding: 10px 11px;

  border: 1px solid #e4e9f0;
  border-radius: 7px;

  background-color: #f8fafc;
  color: #475467;

  font-size: 0.78rem;
  line-height: 1.55;

  white-space: pre-wrap;
  word-break: break-word;

  scrollbar-width: thin;
}

.npc-popup-footer {
  display: flex;
  justify-content: flex-end;

  padding: 11px 17px 14px;

  border-top: 1px solid #edf1f5;
  background-color: #fbfcfd;
}

.npc-popup-done {
  min-width: 70px;
}

/* Popup transition */
.npc-popup-fade-enter-active,
.npc-popup-fade-leave-active {
  transition: opacity 0.18s ease;
}

.npc-popup-fade-enter-active .npc-popup-card,
.npc-popup-fade-leave-active .npc-popup-card {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.npc-popup-fade-enter-from,
.npc-popup-fade-leave-to {
  opacity: 0;
}

.npc-popup-fade-enter-from .npc-popup-card,
.npc-popup-fade-leave-to .npc-popup-card {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}
</style>
